import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Skeleton, Tabs } from "antd";
import { useTranslation } from "react-i18next";

import { userSelectors } from "@fitness-app/app-store";

import TabsContainer from "~/components/TabsContainer/TabsContainer";
import TraineeCalendar from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/TraineeCalendar";
import TraineeChallengesHabits from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeChallengesHabits/TraineeChallengesHabits";
import TraineeEmails from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeEmails/TraineeEmails";
import TraineeMeasurements from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeMeasurements";
import TraineeNotes from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNotes/TraineeNotes";
import TraineeNutrition from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNutrition/TraineeNutrition";
import TraineeProducts from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeProducts";
import TraineeResults from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeResults/TraineeResults";
import TraineeSettings from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeSettings/TraineeSettings";
import TraineeSurveys from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeSurveys/TraineeSurveys";
import TraineeTrainingProgram from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeTrainingProgram/TraineeTrainingProgram";
import { TraineeAiReportsTab } from "~/modules/Trainee/TraineeProfile/components/TraineeAiReports/TraineeAiReports";
import {
  isTraineeFeatureVisible,
  type TraineeFeatureKey,
} from "~/modules/Trainee/TraineeProfile/TraineeFeatures/traineeFeaturePermissions";
import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { CHALLENGES_FEATURE_FLAG } from "~/modules/Challenges/challengesFeatureFlag";
import {
  TraineeStoreGate,
  useTraineeProfile,
  useTraineeScope,
  useTraineeSearchParams,
  useTraineeStoreOwnership,
} from "~/modules/Trainee/TraineeProfile/scope";
import { useAppSelector } from "~/store/initializeStore";

const features = [
  {
    label: "results",
    key: "results",
    Component: TraineeResults,
  },
  {
    label: "challengesHabits",
    key: "challengesHabits",
    Component: TraineeChallengesHabits,
  },
  {
    label: "programs",
    key: "programs",
    Component: TraineeTrainingProgram,
    usesReduxBuilder: true,
  },
  {
    label: "calendar",
    key: "calendar",
    Component: TraineeCalendar,
    usesReduxBuilder: true,
  },
  {
    label: "nutritionPlan",
    key: "nutrition",
    Component: TraineeNutrition,
    usesReduxBuilder: true,
  },
  {
    label: "measurement",
    key: "measurements",
    Component: TraineeMeasurements,
  },
  {
    label: "surveys",
    key: "surveys",
    Component: TraineeSurveys,
  },
  {
    label: "notes",
    key: "notes",
    Component: TraineeNotes,
  },
  {
    label: "products",
    key: "products",
    Component: TraineeProducts,
  },
  {
    label: "emails",
    key: "emails",
    Component: TraineeEmails,
  },
  {
    label: "aiReport",
    key: "aiReport",
    Component: TraineeAiReportsTab,
  },
  {
    label: "settings",
    key: "settings",
    Component: TraineeSettings,
  },
] satisfies Array<{
  label: string;
  key: TraineeFeatureKey;
  Component: React.ComponentType;
  // Zakładka z builderem na Reduxie - renderowana w TraineeStoreGate, patrz scope/TraineeStoreGate.tsx.
  usesReduxBuilder?: boolean;
}>;

// TraineeFeatures renderuje się przy każdym przełączeniu karty (czyta `isActive` i parametry
// widoku). Zakładki nie mają propsów, więc memo odcina tę kaskadę - inaczej np. mapa aktywności
// w „Wynikach" przeliczała setki tooltipów przy każdym przejściu między kartami.
const FeatureContent = memo(({ Component }: { Component: React.ComponentType }) => <Component />);

const TraineeFeatures = () => {
  const { data: profile } = useTraineeProfile();
  const { isActive } = useTraineeScope();
  const permissions = useAppSelector(userSelectors.getUserPermissions);
  const { t } = useTranslation("trainees");
  const { enabled: challengesEnabled } = usePostHogFeatureFlag(CHALLENGES_FEATURE_FLAG);
  const visibleFeatures = features.filter((feature) =>
    isTraineeFeatureVisible(feature.key, permissions, challengesEnabled),
  );
  const [search, setSearchParams] = useTraineeSearchParams();
  // Only honor a ?tab= value that maps to a visible feature.
  const requestedTab = search.get("tab");
  const fallbackTab = visibleFeatures[0]?.key;
  const defaultActiveTab =
    requestedTab && visibleFeatures.some((feature) => feature.key === requestedTab) ? requestedTab : fallbackTab;
  const prevParamTab = useRef<string | null>(null);
  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  useEffect(() => {
    if (defaultActiveTab !== prevParamTab.current && defaultActiveTab) {
      setActiveTab(defaultActiveTab);
    }
    prevParamTab.current = defaultActiveTab || null;
  }, [defaultActiveTab]);

  const showsReduxBuilder = visibleFeatures.some(
    (feature) => feature.key === activeTab && "usesReduxBuilder" in feature && feature.usesReduxBuilder,
  );
  useTraineeStoreOwnership(isActive && showsReduxBuilder);

  const handleTabChange = useCallback(
    (key: string) => {
      setActiveTab(key);
      setSearchParams({ tab: key });
    },
    [setSearchParams],
  );

  if (!profile) {
    return <Skeleton paragraph={{ rows: 5 }} />;
  }

  return (
    <TabsContainer>
      <Tabs
        destroyInactiveTabPane
        tabPosition="top"
        className="min-h-[800px]"
        type="card"
        activeKey={activeTab}
        onChange={handleTabChange}
      >
        {visibleFeatures.map((Feature) => (
          <Tabs.TabPane tab={t(`features.${Feature.label}`)} key={Feature.key}>
            {"usesReduxBuilder" in Feature && Feature.usesReduxBuilder ? (
              <TraineeStoreGate>
                <FeatureContent Component={Feature.Component} />
              </TraineeStoreGate>
            ) : (
              <FeatureContent Component={Feature.Component} />
            )}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </TabsContainer>
  );
};

export default TraineeFeatures;
